export const STALE_CHUNK_RELOAD_KEY = 'dams.staleChunkReloadAt';
export const STALE_CHUNK_RELOAD_WINDOW_MS = 10000;

const INDEX_URL = '/index.html';
const ENTRY_SRC = /^\/assets\/index-[\w-]+\.js$/;

// A tab opened before a deploy may request a hashed chunk that no longer exists (DE-1764).
// Reload only when the deployed index.html names a different entry script than the running page
// (DE-1775); a chunk that fails for any other reason must not reload. The time guard prevents a loop.
// The event is never default-prevented: Vite then resolves the import with undefined, which would
// hide the error from the caller and from Sentry.
export const installStaleChunkReload = ({window, storage, now, reload, fetch, document}) => {
    let checking = false;
    window.addEventListener('vite:preloadError', async () => {
        if (checking) {
            return;
        }
        checking = true;
        try {
            if (!isGuardActive(storage, now) && await isNewBuildDeployed(fetch, document) && stampGuard(storage, now)) {
                reload();
            }
        } catch {
            // A failed check must leave the page as it is.
        } finally {
            checking = false;
        }
    });
};

const isNewBuildDeployed = async (fetch, document) => {
    const running = entryScript(document);
    const response = await fetch(INDEX_URL, {cache: 'no-store'});
    if (!running || !response.ok) {
        return false;
    }
    const deployed = entryScript(new DOMParser().parseFromString(await response.text(), 'text/html'));
    return Boolean(deployed) && deployed !== running;
};

const entryScript = (document) =>
    Array.from(document.querySelectorAll('script[type="module"][src]'))
        .map((script) => script.getAttribute('src'))
        .find((src) => ENTRY_SRC.test(src));

const isGuardActive = (storage, now) => {
    try {
        return isWithinWindow(now() - lastReloadAt(storage));
    } catch {
        return true;
    }
};

const stampGuard = (storage, now) => {
    try {
        storage.setItem(STALE_CHUNK_RELOAD_KEY, String(now()));
        return true;
    } catch {
        return false;
    }
};

// A negative elapsed time means the clock moved back; treat that stamp as expired.
const isWithinWindow = (elapsedMs) => elapsedMs >= 0 && elapsedMs < STALE_CHUNK_RELOAD_WINDOW_MS;

const lastReloadAt = (storage) => {
    const last = Number(storage.getItem(STALE_CHUNK_RELOAD_KEY));
    return Number.isFinite(last) && last > 0 ? last : -Infinity;
};
